import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";

function publicClient() {
  return createClient<Database>(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
  });
}

export type PublicExperiment = {
  id: string;
  key: string;
  variants: {
    id: string;
    key: string;
    label: string;
    blurb: string | null;
    image_url: string | null;
    weight: number;
    is_control: boolean;
  }[];
};

/**
 * Returns experiments that are currently running, with their variants.
 * Public-safe: scoped by RLS on `status = 'running'`.
 */
export const listRunningExperiments = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicExperiment[]> => {
    const sb = publicClient();
    const { data, error } = await sb
      .from("experiments")
      .select("id, key, experiment_variants (id, key, label, blurb, image_url, weight, is_control)")
      .eq("status", "running");
    if (error || !data) return [];
    return data.map((e: any) => ({
      id: e.id,
      key: e.key,
      variants: (e.experiment_variants ?? []).map((v: any) => ({
        id: v.id,
        key: v.key,
        label: v.label,
        blurb: v.blurb,
        image_url: v.image_url,
        weight: v.weight,
        is_control: v.is_control,
      })),
    }));
  },
);

/* -------------------- admin -------------------- */

async function assertAdmin(supabase: any, userId: string) {
  const { data, error } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
  if (error || data !== true) throw new Error("Forbidden");
}

export type AdminExperiment = {
  id: string;
  key: string;
  name: string;
  description: string | null;
  status: "draft" | "running" | "paused";
  variants: {
    id: string;
    key: string;
    label: string;
    blurb: string | null;
    image_url: string | null;
    weight: number;
    is_control: boolean;
  }[];
};

export const adminListExperiments = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<AdminExperiment[]> => {
    await assertAdmin(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("experiments")
      .select("id, key, name, description, status, experiment_variants (id, key, label, blurb, image_url, weight, is_control)")
      .order("key");
    if (error) throw new Error(error.message);
    return (data ?? []).map((e: any) => ({
      id: e.id,
      key: e.key,
      name: e.name,
      description: e.description,
      status: e.status,
      variants: (e.experiment_variants ?? [])
        .slice()
        .sort((a: any, b: any) => (a.is_control === b.is_control ? a.key.localeCompare(b.key) : a.is_control ? -1 : 1))
        .map((v: any) => ({
          id: v.id,
          key: v.key,
          label: v.label,
          blurb: v.blurb,
          image_url: v.image_url,
          weight: v.weight,
          is_control: v.is_control,
        })),
    }));
  });

export const adminSetExperimentStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string; status: "draft" | "running" | "paused" }) => {
    if (!data.id) throw new Error("Missing experiment id");
    if (!["draft", "running", "paused"].includes(data.status)) throw new Error("Bad status");
    return data;
  })
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await context.supabase
      .from("experiments")
      .update({ status: data.status })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

export const adminUpsertVariant = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: {
    id?: string;
    experiment_id: string;
    key: string;
    label: string;
    blurb?: string | null;
    image_url?: string | null;
    weight: number;
    is_control: boolean;
  }) => {
    if (!data.experiment_id) throw new Error("Missing experiment_id");
    if (!data.key || data.key.length > 32) throw new Error("Variant key required");
    if (!data.label) throw new Error("Label required");
    if (data.weight < 0 || data.weight > 1000) throw new Error("Weight out of range");
    return data;
  })
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    const row = {
      experiment_id: data.experiment_id,
      key: data.key,
      label: data.label,
      blurb: data.blurb ?? null,
      image_url: data.image_url ?? null,
      weight: data.weight,
      is_control: data.is_control,
    };
    const query = data.id
      ? context.supabase.from("experiment_variants").update(row).eq("id", data.id).select("id").maybeSingle()
      : context.supabase.from("experiment_variants").insert(row).select("id").maybeSingle();
    const { data: out, error } = await query;
    if (error) throw new Error(error.message);
    return { id: out?.id as string };
  });

export const adminDeleteVariant = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string }) => {
    if (!data.id) throw new Error("Missing variant id");
    return data;
  })
  .handler(async ({ data, context }) => {
    await assertAdmin(context.supabase, context.userId);
    // Don't allow deleting the control.
    const { data: row } = await context.supabase
      .from("experiment_variants")
      .select("is_control")
      .eq("id", data.id)
      .maybeSingle();
    if (row?.is_control) throw new Error("Cannot delete the control variant");
    const { error } = await context.supabase.from("experiment_variants").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

export type ExperimentResultsRow = {
  variant_id: string;
  variant_key: string;
  label: string;
  is_control: boolean;
  impressions: number;
  add_to_cart: number;
  begin_checkout: number;
  order_completed: number;
  revenue_cents: number;
};

export const adminGetResults = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { experiment_id: string }) => {
    if (!data.experiment_id) throw new Error("Missing experiment_id");
    return data;
  })
  .handler(async ({ data, context }): Promise<ExperimentResultsRow[]> => {
    await assertAdmin(context.supabase, context.userId);
    const { data: variants, error: vErr } = await context.supabase
      .from("experiment_variants")
      .select("id, key, label, is_control")
      .eq("experiment_id", data.experiment_id)
      .order("is_control", { ascending: false })
      .order("key");
    if (vErr) throw new Error(vErr.message);
    if (!variants?.length) return [];

    const { data: events, error: eErr } = await context.supabase
      .from("experiment_events")
      .select("variant_id, event_type, value_cents")
      .eq("experiment_id", data.experiment_id);
    if (eErr) throw new Error(eErr.message);

    const byVariant = new Map<string, ExperimentResultsRow>();
    for (const v of variants) {
      byVariant.set(v.id, {
        variant_id: v.id,
        variant_key: v.key,
        label: v.label,
        is_control: v.is_control,
        impressions: 0,
        add_to_cart: 0,
        begin_checkout: 0,
        order_completed: 0,
        revenue_cents: 0,
      });
    }
    for (const ev of events ?? []) {
      const row = byVariant.get(ev.variant_id as string);
      if (!row) continue;
      switch (ev.event_type) {
        case "impression":
          row.impressions += 1;
          break;
        case "add_to_cart":
          row.add_to_cart += 1;
          break;
        case "begin_checkout":
          row.begin_checkout += 1;
          break;
        case "order_completed":
          row.order_completed += 1;
          row.revenue_cents += ev.value_cents ?? 0;
          break;
      }
    }
    return Array.from(byVariant.values());
  });
